import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import ToggleOption from '@/components/common/ToggleOption/ToggleOption';
import { EDITABLE_STATUS_KEYS } from '@/config/statuses.config';
import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import CustomToggleTitleComponent from './components/CustomToggleTitleComponent';
import DirectDebitModal from './components/DirectDebitModal';
import useUpdateDirectDebit from './hooks/useUpdateDirectDebit';
import { useGetLatestFieldVerifications }
  from '@/pages/ContractDetails/ContractHeaders/hooks/useGetLatestFieldVerifications';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';
import VerificationTooltipIcon from '@/components/common/ContractHeaderItem/VerificationTooltipIcon';
import { fieldVerificationRefreshTrigger } from '@/pages/ContractDetails/ContractIq/ContractIq.state';
import { FieldVerificationTypeEnum } from '@/graphql/contracts/_generated_/contracts';

export default function DirectDebitContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const contract = useGetContractDetailsFromCache(contractId);
  const [isEnabled, setIsEnabled] = useState(Boolean(contract?.directDebit?.type));
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const { updateDirectDebit } = useUpdateDirectDebit(contractId);
  const refreshTrigger = useRecoilValue(fieldVerificationRefreshTrigger);
  const { getLatestFieldVerifications, fieldVerificationMap } = useGetLatestFieldVerifications();
  const { searchUsers } = useSearchUsers();
  const [verificationUsers, setVerificationUsers] = useState<User[]>([]);
  const fetchedUserIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (contractId) {
      getLatestFieldVerifications(contractId);
    }
  }, [contractId, getLatestFieldVerifications, refreshTrigger]);

  useEffect(() => {
    const userIds = Object.values(fieldVerificationMap).map((v) => v.userID);
    const uniqueNewUserIds = [...new Set(userIds)].filter(
      (id) => !fetchedUserIdsRef.current.has(id)
    );

    if (uniqueNewUserIds.length > 0) {
      uniqueNewUserIds.forEach((id) => fetchedUserIdsRef.current.add(id));
      searchUsers({ ids: uniqueNewUserIds }).then((searchResult) => {
        setVerificationUsers((prev) => {
          const existingUserIds = new Set(prev.map((u) => u.Id));
          const newUsers = searchResult.filter((u) => !existingUserIds.has(u.Id));
          return [...prev, ...newUsers];
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldVerificationMap]);

  const renderDirectDebitVerificationIcon = () => {
    const verification = fieldVerificationMap?.['Direct Debit'];
    if (
      !verification
      || verification.type === FieldVerificationTypeEnum.UNVERIFY
      || verification.type === FieldVerificationTypeEnum.REMOVED
    ) return null;

    const user = verificationUsers?.find((u) => u.Id === verification.userID);
    const userName = user
      ? `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim() || 'Unknown'
      : 'Loading...';

    return (
      <VerificationTooltipIcon
        userName={userName}
        verifiedAt={verification.verifiedAt}
      />
    );
  };

  const handleChangeToggle = () => {
    if (isEnabled) {
      updateDirectDebit(null);
    }
    else {
      setIsVisibleModal(true);
    }
  };

  useEffect(() => {
    setIsEnabled(Boolean(contract?.directDebit?.type));
    setIsVisibleModal(false);
  }, [contract]);

  if (!contract || !EDITABLE_STATUS_KEYS.includes(contract.status.status)) {
    return null;
  }

  return (
    <>
      {isVisibleModal && (
        <DirectDebitModal
          contract={contract}
          onClose={() => setIsVisibleModal(false)}
          onSubmit={updateDirectDebit}
        />
      )}

      <ToggleOption
        id="direct-debit"
        isEnable={isEnabled}
        onChange={handleChangeToggle}
        title={(
          <CustomToggleTitleComponent
            title="Direct Debit"
            isEnabledToggle={isEnabled}
            onClose={() => setIsVisibleModal(true)}
            selectedOptionText={contract?.directDebit?.formattedType ?? null}
            verificationIcon={renderDirectDebitVerificationIcon()}
          />
        )}
      />
    </>
  );
}
